import React, { useState, useEffect, useContext } from "react";
import {
    Typography,
    DatePicker,
    Spin,
    Layout,
    Form,
    Button,
    Space,
} from "antd";
import { ProfileOutlined, FileExcelOutlined } from "@ant-design/icons";
import moment from "moment";
import { Table } from "ant-table-extensions";
import { errorN } from "../../general";
import { navigate } from "hookrouter";
import useFetch from "use-http";
import Store, { Context } from "../../store";
import { ShowReport } from "../Bookings/function";

const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Text } = Typography;

const formatter = new Intl.NumberFormat("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

function TaxSum() {
    const [state] = useContext(Context);
    const { get, response, loading } = useFetch();

    const [form] = Form.useForm();
    const [data, setData] = useState([]);
    const [taxTypes, setTaxTypes] = useState([]);
    const [range, setRange] = useState("");

    async function loadData() {
        try {
            const values = await form.validateFields();

            setRange(
                `From ${moment(values.dtDateRange[0]).format("DD-MM-YYYY")} to ${moment(
                    values.dtDateRange[1]
                ).format("DD-MM-YYYY")}`
            );

            const property = JSON.parse(localStorage.getItem("currentproperty"));
            const s = moment. utc(values.dtDateRange[0]).format("YYYY-MM-DD");
            const e = moment.utc(values.dtDateRange[1]).format("YYYY-MM-DD");

            const list = await get(
                `/api/hotels/${property.id}/invoices/payments?start=${s}T00:00:00Z&end=${e}T23:59:59Z`
            );

            if (! response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("session");
                    window.location.reload();
                }
                return;
            }

            const invoiceMap = {};
            const taxSet = new Set();

            list.forEach(inv => {
                const key = inv.id;

                //console.log('invoice item:', inv)

                if (! invoiceMap[key]) {
                    invoiceMap[key] = {
                        key,
                        date: moment(inv.created).format("YYYY-MM-DD hh:mm:ss A"),
                        no: inv.no,
                        name: inv.data.payor.name,
                        totalTax: 0,
                        subtotal: inv.amount - (inv.rounding || 0),
                        rounding: inv.rounding || 0,
                        total: inv.amount,
                    };
                }

                inv.breakdown.tax.forEach(t => {
                    taxSet.add(t.name);
                    // 🔥 FIX:  Flatten structure - use direct property instead of nested
                    const taxKey = `tax_${t.name}`;
                    invoiceMap[key][taxKey] = (invoiceMap[key][taxKey] || 0) + t.rate;
                    invoiceMap[key].totalTax += t.rate;
                    invoiceMap[key].subtotal = invoiceMap[key].subtotal - t.rate;
                });
            });

            setTaxTypes([...taxSet]);
            setData(Object.values(invoiceMap));
        } catch (err) {
            errorN(`Error Load Data: ${err}`);
        }
    }

    useEffect(() => {
        if (!localStorage.getItem("session")) {
            navigate("/");
        }
    }, []);

    // base columns
    const baseColumns = [
        {
            width: 120,
            title: "Date",
            dataIndex: "date",
            key: "date",
            sorter: (a, b) => a.date.localeCompare(b.date),
            formatter: v => v, // REQUIRED for CSV
        },
        {
            width: 120,
            title: "Invoice No.",
            dataIndex: "no",
            key: "no",
            sorter: (a, b) => a.no.localeCompare(b.no),
            formatter: v => v,
        },
        {
            width: 180,
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),
            formatter: v => v,
        },
    ];

    const invoiceColumns = [
        {
            width: 120,
            title: "Subtotal",
            dataIndex: `subtotal`,
            key: "subtotal",
            render: v => formatter.format(v || 0),
            formatter: v => formatter.format(v || 0),
        },
        {
            width: 120,
            title: "Rounding",
            dataIndex: `rounding`, // 🔥 FIX: Direct property access
            key: "rounding",
            render: v => formatter.format(v || 0),
            formatter: v => formatter.format(v || 0), // Now this will work
        },
        {
            width: 120,
            title: "Total",
            dataIndex: `total`, // 🔥 FIX: Direct property access
            key: "total",
            render: v => formatter.format(v || 0),
            formatter: v => formatter.format(v || 0), // Now this will work
        }
    ];

    // dynamic tax columns (VERY IMPORTANT)
    const taxColumns = taxTypes.map(tax => ({
        width: 120,
        title: tax,
        dataIndex: `tax_${tax}`, // 🔥 FIX: Direct property access
        key: tax,
        render: v => formatter.format(v || 0),
        formatter: v => formatter.format(v || 0), // Now this will work
    }));

    // total tax column
    const totalTaxColumn = {
        width: 140,
        title: "Total Tax",
        dataIndex: "totalTax",
        key: "totalTax",
        render: v => formatter.format(v || 0),
        formatter: v => formatter.format(v || 0),
        sorter: (a, b) => a.totalTax - b.totalTax,
    };

    // FINAL columns
    const columns = [
        ...baseColumns,
        ...taxColumns,
        totalTaxColumn,
        ...invoiceColumns
    ];

    const sanitizeExportColumns = cols =>
    cols.map(col => ({
        ...col,
        formatter:
            typeof col.formatter === "function"
                ? col.formatter
                : v => v ?? "",
    }));



    return (
        <>
            <div
                style={{
                    backgroundImage: `url("/header-bg7.png")`,
                    padding: "15px",
                    fontSize: "18px",
                }}
            >
                <ProfileOutlined /> &nbsp; Tax Summary
            </div>

            <Layout>
                <Spin spinning={loading}>
                    <Content style={{ backgroundColor: "white" }}>
                        <Form form={form} layout="vertical">
                            <div style={{ padding: 20, height: "73vh", overflow: "auto" }}>
                                <Space>
                                    <Form.Item
                                        label="Arrival - Departure"
                                        name="dtDateRange"
                                        rules={[{ required: true, message: "Dates missing!" }]}
                                    >
                                        <RangePicker />
                                    </Form.Item>

                                    <Button type="primary" onClick={loadData}>
                                        Load
                                    </Button>

                                    <ShowReport
                                        data={data}
                                        name={"Tax Summary"}
                                        range={range}
                                        no={"13"}
                                        taxTypes={taxTypes}
                                    />

                                </Space>

                                <Table
                                    columns={columns}
                                    dataSource={data}
                                    exportable
                                    exportableProps={{
                                        showColumnPicker: true,
                                        fileName: "TaxSummary-List",
                                        columns: sanitizeExportColumns(columns), // 🔥 FIX
                                        btnProps: {
                                            type: "primary",
                                            icon: <FileExcelOutlined />,
                                        },
                                    }}
                                    bordered
                                    size="small"
                                    scroll={{ x: 1500, y: 360 }}
                                    pagination={false}
                                    footer={pageData => {
                                        const grandTotal = pageData.reduce(
                                            (sum, r) => sum + r.totalTax,
                                            0
                                        );
                                        return (
                                            <Text strong>
                                                Grand Total Tax: {formatter.format(grandTotal)}
                                            </Text>
                                        );
                                    }}
                                />

                            </div>
                        </Form>
                    </Content>
                </Spin>
            </Layout>
        </>
    );
}

export default TaxSum;
